import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { OrderInfo } from './order/OrderInfo';
import { Order } from '../../../models';
import { useNavigate, useQuery } from '../../../routes';
import { payment } from '../../../layout/routes';
import { useLaunchDarkly } from '../../../launchdarkly';
import { CompletePayment } from '../../../components';
import CancelOrderDialog from './CancelOrderDialog';
import { fromLayout, fromOrder, useAppDispatch } from '../../../store';
import { LoadingProgress } from '../../../layout';

export const PendingOrderHistory = (
  { pendingOrders }: { pendingOrders: Order[] },
) => {
  const { flags } = useLaunchDarkly();
  const { queryDictionary } = useQuery();
  const { navigate, replaceQuery } = useNavigate();
  const dispatch = useAppDispatch();
  const [showCancelOrderDialog, setShowCancelOrderDialog] = useState(false);
  const [selectedCancelOrder, setSelectedCancelOrder] = useState<Order>();
  const [cancelLoading, setCancelLoading] = useState(false);

  const cancelOrder = (order?: Order) => {
    setShowCancelOrderDialog(false);
    if (order) {
      const cancelOrder = async () => {
        setCancelLoading(true);
        await dispatch(fromOrder.doCancelOrder(order.id));
        setCancelLoading(false);
      };
      cancelOrder()
        .then(() => {
          dispatch(fromLayout.setRefetchData(true));
        })
        .catch(() => {});
    }
  };

  const showCancelDialog = (order: Order) => {
    setShowCancelOrderDialog(true);
    setSelectedCancelOrder(order);
  };

  const closeCancelDialog = () => {
    setShowCancelOrderDialog(false);
  };

  const renderActionBtnForBT = (order: Order) => (
    <Box
      id={`order-${order.id}-action-items`}
      display="flex"
      flexDirection="row"
      justifyContent="flex-end"
      paddingTop={1}
    >
      <Button
        variant="contained"
        onClick={() => navigate(payment.path, replaceQuery(queryDictionary()))}
        sx={{
          marginLeft: 2,
          backgroundColor: '#43509B',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#3A4A8D',
          },
        }}
      >
        <FormattedMessage id="page.order.history.action.button.update.payment.method" />
      </Button>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          // TODO
        }}
        sx={{
          marginLeft: 2,
          paddingX: 2,
          borderColor: '#D14343',
          backgroundColor: 'transparent',
          color: '#D14343',
          '&:hover': {
            borderColor: '#D14243',
            backgroundColor: 'transparent',
          },
        }}
      >
        <FormattedMessage id="page.order.history.action.button.request.cancel" />
      </Button>
    </Box>
  );

  const renderActionBtnForCreditCard = (order: Order) => (
    <Box
      id={`order-${order.id}-action-items`}
      display="flex"
      flexDirection="row"
      justifyContent="flex-end"
      paddingTop={1}
    >
      <CompletePayment
        order={order}
      />
      {flags?.cancelCreditCardOrderEnabled && (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => showCancelDialog(order)}
          sx={{
            marginLeft: 2,
            paddingX: 2,
            borderColor: '#D14343',
            backgroundColor: 'transparent',
            color: '#D14343',
            '&:hover': {
              borderColor: '#D14243',
              backgroundColor: 'transparent',
            },
          }}
        >
          <FormattedMessage id="page.order.history.action.button.cancel.order" />
        </Button>
      )}
    </Box>
  );

  const renderActionBtns = (order: Order) => (
    order.stripeInvoiceUrl ? renderActionBtnForCreditCard(order)
      : (flags?.orderHistoryActionBtnEnabled && renderActionBtnForBT(order))
  );

  const renderOrderDetails = () => pendingOrders.map(order => (
    <OrderInfo
      key={`pending-order-${order.id}`}
      order={order}
      actionItems={renderActionBtns(order)}
    />
  ));

  return (
    <Box
      id="pending-order-container"
      display="flex"
      flexDirection="column"
    >
      <Box id="pending-order-title">
        <Typography paddingY={2} fontSize={24} fontWeight={600} color="#43509B" lineHeight="135%">
          <FormattedMessage id="page.order.history.pending.order.title" />
        </Typography>
      </Box>
      {renderOrderDetails()}
      <CancelOrderDialog
        showDialog={showCancelOrderDialog}
        handleCancelOrder={() => cancelOrder(selectedCancelOrder)}
        handleCloseDialog={() => closeCancelDialog()}
      />
      {cancelLoading && <LoadingProgress fullscreen />}
    </Box>
  );
};
